/**
 * Which words the AI ask says, for the version of the text this build holds (#399).
 *
 * The deck (`copy/firstRun.ts`) keeps the strings of every version side by side: `consentAiBody` and
 * `consentAiWhere` are version 1, `consentAiBody2` and `consentAiWhere2` are version 2 (CS-D17, #404;
 * nobody who answered version 1 is asked again, CO-D1). `CONSENT_TEXT_VERSION.aiProcessing` says which one this build shows and
 * records, so the two cannot be chosen apart: a build that recorded "version 2" over version 1's
 * sentences would have the person agree to words they were not shown. **Raising the version without
 * adding its row here throws**, and `consentCopy.test.ts` fails first.
 *
 * Nothing here is a string of its own. Every sentence is a deck symbol, byte for byte.
 */

import { CONSENT_TEXT_VERSION, COPY, fill } from '../../copy/firstRun'
import type { ConsentState, HealthText } from '../../api/consents'

export interface AiConsentText {
  /** The sentence under the question: what goes where. */
  body: string
  /** WHERE, in the full text. */
  where: string
}

const AI_TEXT: Record<number, AiConsentText> = {
  1: { body: COPY.consentAiBody, where: COPY.consentAiWhere },
  2: { body: COPY.consentAiBody2, where: COPY.consentAiWhere2 },
}

/** The AI consent's text for a version of it. Throws for a version this deck has no words for. */
export function aiConsentText(version: number = CONSENT_TEXT_VERSION.aiProcessing): AiConsentText {
  const text = AI_TEXT[version]
  if (!text) throw new Error(`The deck has no AI consent text for version ${version}.`)
  return text
}

/** The full text of one consent, one tap away from its ask (CS-D16): four numbered facts, then what a no costs and how to withdraw. */
export interface FullText {
  /** The consent's name as the deck words it ("AI processing"); the dialog's eyebrow capitalises it. */
  name: string
  version: number
  /** The dialog's title, where it is not "The full text" (the analytics text's is "Cookie details"). */
  title?: string
  /** A section before the facts, for what needs no agreement (the analytics text's functional cookies). */
  lead?: { title: string; text: string }
  /** A title over the numbered facts, when a `lead` sits before them ("Analytics, optional"). */
  factsTitle?: string
  /**
   * `text` is one paragraph. A fact whose text is several (the analytics WHAT: the public pages, the web app,
   * everywhere) gives them as `parts` instead, each led by its label up to the colon, which the dialog sets in the
   * heavier weight; it is still one string of the deck.
   */
  facts: { label: string; text?: string; parts?: string[] }[]
  closing: string[]
}

/** The AI consent's full text for a version of it: what the lawyer reads (#403), word for word the deck's. */
export function aiFullText(version: number = CONSENT_TEXT_VERSION.aiProcessing): FullText {
  const text = aiConsentText(version)
  return {
    name: COPY.consentAiName,
    version,
    facts: [
      { label: COPY.consentWhatLabel, text: COPY.consentAiWhat },
      { label: COPY.consentWhoLabel, text: COPY.consentAiWho },
      { label: COPY.consentWhereLabel, text: text.where },
      { label: COPY.consentWhyLabel, text: COPY.consentAiWhy },
    ],
    closing: [COPY.consentAiNo, COPY.consentAiLater],
  }
}

/**
 * The Health consent's full text for one of its two texts (CS-D16, AL-D7, FR2-D12 (b)): the iPhone's ask shows the iOS
 * text as a sheet, and the web's Privacy tab shows the text a person answered on request. One text for everyone on a
 * platform, whether or not a Watch is paired.
 *
 * `ios` (the default) is the text iOS and the web show: Apple Health, 90 days, the Watch. `android` is Android's own:
 * Health Connect, 30 days, no Watch. Its three lines that differ are the deck's `...Android` symbols, byte for byte the
 * Kotlin deck's; its who, where and how to withdraw are the shared ones. Each is labelled with the number of the text it
 * is, this deck's (`CONSENT_TEXT_VERSION.healthData` and `.healthDataAndroid`), which `scripts/check_copy_decks.py` holds
 * to the server's constants, so the words and the number under them cannot be raised apart (run 119's ruling R12, which
 * replaced the server's `current_version` as the label). `version` is for a test that wants another number.
 */
export function healthFullText(text: HealthText = 'ios', version?: number): FullText {
  const android = text === 'android'
  return {
    name: COPY.consentHealthName,
    version: version ?? (android ? CONSENT_TEXT_VERSION.healthDataAndroid : CONSENT_TEXT_VERSION.healthData),
    facts: [
      { label: COPY.consentWhatLabel, text: android ? COPY.consentHealthWhatAndroid : COPY.consentHealthWhat },
      { label: COPY.consentWhoLabel, text: COPY.consentHealthWho },
      { label: COPY.consentWhereLabel, text: COPY.consentHealthWhere },
      { label: COPY.consentWhyLabel, text: android ? COPY.consentHealthWhyAndroid : COPY.consentHealthWhy },
    ],
    closing: [android ? COPY.consentHealthNoAndroid : COPY.consentHealthNo, COPY.consentHealthLater],
  }
}

/**
 * Where a Health text was agreed to, for the mono line under a row's state: the phone that shows that text. Drawn from the
 * row's own text, whatever its status, when two Health rows show (run 119's ruling R12): a declined or withdrawn answer
 * keeps no client, so `consentWhereLine` has nothing to say for it, and two rows of one name must still say which phone
 * each is.
 */
export function healthWhereLine(text: HealthText): string {
  return text === 'android' ? COPY.consentWhereAndroid : COPY.consentWhereIphone
}

/**
 * The date a consent was answered, as the artboards draw it ("9 Oct 2026"), in the viewer's own time
 * zone unless one is given (a test pins it). `iso` is the server's UTC instant.
 */
export function consentDateLine(iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso))
}

/**
 * The state line of a Privacy row (CS-D9): "Agreed 9 Oct 2026 · Version 1", "Declined 9 Oct 2026",
 * "Withdrawn 9 Oct 2026" or "Not asked yet". A record that cannot be put into the deck's words (an
 * agreement with no day or no version, which the server never sends) gives the empty string rather
 * than a date it does not have.
 */
export function consentStateLine(state: ConsentState, timeZone?: string): string {
  switch (state.status) {
    case 'granted':
      return state.since && state.version != null
        ? fill(COPY.consentAgreedOn, { date: consentDateLine(state.since, timeZone), n: state.version })
        : ''
    case 'declined':
      return state.declined_at
        ? fill(COPY.consentDeclinedOn, { date: consentDateLine(state.declined_at, timeZone) })
        : ''
    case 'withdrawn':
      return state.withdrawn_at
        ? fill(COPY.consentWithdrawnOn, { date: consentDateLine(state.withdrawn_at, timeZone) })
        : ''
    default:
      return COPY.consentNeverAsked
  }
}

/** Where an agreement was given, for the mono line under the state: nothing for a client this build does not know, and nothing for a consent that is not agreed (the record keeps no client on a no, so a no carried in from the browser reads like any other). */
export function consentWhereLine(state: ConsentState): string | null {
  if (state.status !== 'granted') return null
  if (state.client === 'ios') return COPY.consentWhereIphone
  if (state.client === 'android') return COPY.consentWhereAndroid
  if (state.client === 'web') return COPY.consentWhereBrowser
  // An analytics answer the account took from the cookie at sign-in (AA-D6): given in this browser, as a visitor.
  if (state.client === 'web_visitor') return COPY.consentWhereBeforeSignIn
  return null
}
